"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.crudUpdateReply = exports.crudListReply = exports.crudGetReply = exports.crudCreateReply = exports.crudIdParamsBody = exports.crudUnboundedListQueryBody = exports.crudBoundedListQueryBody = exports.crudAggregateReplyBody = exports.crudListReplyBody = exports.crudGetReplyBody = exports.crudUpdateReplyWithItemBody = exports.crudUpdateReplyBody = exports.crudAckReplyBody = exports.crudCreateReplyBody = void 0;
const zod_1 = require("zod");
const compose_1 = require("./compose");
const error_codes_1 = require("./error-codes");
/**
 * Helper-фабрики zod-схем для типовых форм CRUD-reply/query.
 *
 * Все фабрики возвращают **готовые** zod-схемы которые можно передавать в
 * `defineCommand`/`defineQuery` (или typed-обёртки из `define-crud.ts`).
 *
 * Они НЕ добавляют envelope-полей (`type: z.literal(...)`, `version: z.literal(1)`,
 * `commandId/queryId/eventId`, `tenantId`, `timestamp`) — это делает caller через
 * `CommandMessage.extend({...})`/`QueryMessage.extend({...})`/`ReplyMessage.extend(...)`.
 *
 * @example
 *   // Reply create — discriminated {isOk:true,item} | {isOk:false,errorCode}
 *   FooCreateReplyV1 = ReplyMessage.extend({
 *     type: z.literal('FooCreateReply'),
 *     version: z.literal(1),
 *   }).and(crudCreateReplyBody(FooSnapshot, CrudErrorCodes));
 */
// ── Reply body builders (без envelope) ──
/**
 * Body для create-reply: `{isOk:true, item: TSnapshot, ...extraOkFields?}` | `{isOk:false, errorCode, error?, details?}`.
 *
 * `extraOkFields` (optional) — доменное расширение success-branch'а. Используется
 * когда entity требует доп. поле в reply (например Session: `created: boolean`
 * для externalKey get-or-create семантики).
 *
 * Без `extraOkFields` — success = `{isOk:true, item}`. С — `{isOk:true, item, ...extra}`.
 *
 * @example Standard:
 *   FooCreateReply = crudCreateReplyBody(FooSnapshot, CrudErrorCodes);
 *
 * @example С domain-extension:
 *   SessionCreateReply = crudCreateReplyBody(SessionSnapshot, CrudErrorCodes, {
 *     created: z.boolean(),  // true=новая row, false=найдена по externalKey
 *   });
 */
const crudCreateReplyBody = (snapshotSchema, errorCodes, extraOkFields) => zod_1.z.discriminatedUnion('isOk', [
    zod_1.z.object({
        isOk: zod_1.z.literal(true),
        item: snapshotSchema,
        ...(extraOkFields ?? {}),
    }),
    zod_1.z.object({
        isOk: zod_1.z.literal(false),
        errorCode: zod_1.z.enum(errorCodes),
        error: zod_1.z.string().optional(),
        details: zod_1.z.record(zod_1.z.unknown()).optional(),
    }),
]);
exports.crudCreateReplyBody = crudCreateReplyBody;
/**
 * Body для update/delete reply: `{isOk:true}` | `{isOk:false, errorCode}`.
 * Без полезной нагрузки на success — операция не возвращает данных.
 */
const crudAckReplyBody = (errorCodes) => zod_1.z.discriminatedUnion('isOk', [
    zod_1.z.object({ isOk: zod_1.z.literal(true) }),
    zod_1.z.object({
        isOk: zod_1.z.literal(false),
        errorCode: zod_1.z.enum(errorCodes),
        error: zod_1.z.string().optional(),
        details: zod_1.z.record(zod_1.z.unknown()).optional(),
    }),
]);
exports.crudAckReplyBody = crudAckReplyBody;
/**
 * CRUDCAS: маркер «условие `where` вычислено». Ставит ядро, которое `where`
 * понимает, — на ok / `conflict` / `not_found` ответ условной записи.
 * Старое ядро незнакомое `where` молча выкидывает (update-команда не
 * `.strict()`) и пишет безусловно; клиент, пославший `where`, без маркера
 * обязан считать запись НЕусловной и отказать громко (O6).
 */
const conditionalMarker = zod_1.z.literal(true).optional();
/**
 * Body стандартного update-reply (CRUDCAS):
 * - `{isOk:true, conditional?, item?}` — `item` (снимок после записи) отдаётся
 *   на условную запись; безусловная, как и раньше, — без полезной нагрузки;
 * - `{isOk:false, errorCode, error?, details?, conditional?, current?}` —
 *   `current` при `errorCode:'conflict'` условной записи: строка на момент
 *   отказа (условие не совпало — штатный исход, не сбой, O3).
 */
const crudUpdateReplyBody = (itemSchema, errorCodes) => zod_1.z.discriminatedUnion('isOk', [
    zod_1.z.object({
        isOk: zod_1.z.literal(true),
        conditional: conditionalMarker,
        item: itemSchema.optional(),
    }),
    zod_1.z.object({
        isOk: zod_1.z.literal(false),
        errorCode: zod_1.z.enum(errorCodes),
        error: zod_1.z.string().optional(),
        details: zod_1.z.record(zod_1.z.unknown()).optional(),
        conditional: conditionalMarker,
        current: itemSchema.optional(),
    }),
]);
exports.crudUpdateReplyBody = crudUpdateReplyBody;
/**
 * Body для update-reply-with-item: `{isOk:true, item: TSnapshot}` | `{isOk:false, errorCode}`.
 * Opt-in альтернатива `crudUpdateReplyBody` — handler возвращает snapshot
 * после мутации всегда, клиент не делает отдельный GET round-trip.
 * Поля условной записи (`conditional`, `current`) — те же, что у `crudUpdateReplyBody`.
 */
const crudUpdateReplyWithItemBody = (snapshotSchema, errorCodes) => zod_1.z.discriminatedUnion('isOk', [
    zod_1.z.object({
        isOk: zod_1.z.literal(true),
        conditional: conditionalMarker,
        item: snapshotSchema,
    }),
    zod_1.z.object({
        isOk: zod_1.z.literal(false),
        errorCode: zod_1.z.enum(errorCodes),
        error: zod_1.z.string().optional(),
        details: zod_1.z.record(zod_1.z.unknown()).optional(),
        conditional: conditionalMarker,
        current: snapshotSchema.optional(),
    }),
]);
exports.crudUpdateReplyWithItemBody = crudUpdateReplyWithItemBody;
/**
 * Body для get(id) reply: `{isOk:true, item: T | null}`.
 *
 * `item: null` — сущность не найдена. Это бизнес-ответ, не ошибка — caller
 * сам решает бросать исключение или обработать штатно. `isOk:true` — единый
 * инфраструктурный дискриминант для всех reply (create/update/delete/get).
 */
const crudGetReplyBody = (snapshotSchema) => zod_1.z.object({ isOk: zod_1.z.literal(true), item: snapshotSchema.nullable() });
exports.crudGetReplyBody = crudGetReplyBody;
/**
 * Body для list reply: `{isOk:true, items, total}` | `{isOk:false, errorCode}`.
 *
 * Консистентно с остальными CRUD-reply (create/update/delete/get) —
 * единый дискриминант `isOk`. Cursor-пагинация (если понадобится) —
 * отдельный route `listCursor`, не union в reply.
 */
const crudListReplyBody = (itemSchema, errorCodes) => {
    const codes = (errorCodes ?? error_codes_1.CrudErrorCodes);
    return zod_1.z.discriminatedUnion('isOk', [
        zod_1.z.object({
            isOk: zod_1.z.literal(true),
            items: zod_1.z.array(itemSchema),
            total: zod_1.z.number().int().nonnegative(),
        }),
        zod_1.z.object({
            isOk: zod_1.z.literal(false),
            errorCode: zod_1.z.enum(codes),
            error: zod_1.z.string().optional(),
            details: zod_1.z.record(zod_1.z.unknown()).optional(),
        }),
    ]);
};
exports.crudListReplyBody = crudListReplyBody;
/**
 * Body для aggregate reply: `{isOk:true, groups, total}` | `{isOk:false, errorCode}`.
 *
 * `groups` — строки-группы: ключи — алиасы groupBy ∪ aggr из запроса (динамика,
 * поэтому `z.record`), значения — скаляры (числа агрегатов, ISO-строки
 * время-бакетов, значения groupBy-колонок). `total` — всего групп без учёта
 * limit/offset (для пагинации по группам).
 */
const crudAggregateReplyBody = (errorCodes) => {
    const codes = (errorCodes ?? error_codes_1.CrudErrorCodes);
    return zod_1.z.discriminatedUnion('isOk', [
        zod_1.z.object({
            isOk: zod_1.z.literal(true),
            groups: zod_1.z.array(zod_1.z.record(zod_1.z.union([zod_1.z.string(), zod_1.z.number(), zod_1.z.boolean(), zod_1.z.null()]))),
            total: zod_1.z.number().int().nonnegative(),
        }),
        zod_1.z.object({
            isOk: zod_1.z.literal(false),
            errorCode: zod_1.z.enum(codes),
            error: zod_1.z.string().optional(),
            details: zod_1.z.record(zod_1.z.unknown()).optional(),
        }),
    ]);
};
exports.crudAggregateReplyBody = crudAggregateReplyBody;
// ── Sort entry schema builder ──
const crudSortEntrySchema = (sortFields) => zod_1.z.object({
    field: zod_1.z.enum(sortFields),
    dir: zod_1.z.enum(['asc', 'desc']),
});
// ── Query body builders ──
/**
 * Body bounded-list query: limit/offset/sort — все optional.
 * Дефолт = полный snapshot (limit как защита-cap, не обязательная пагинация).
 *
 * `sort` — массив `{field, dir}` для multi-field сортировки. `field` —
 * z.enum допустимых полей (whitelist, не free string).
 *
 * @param filtersSchema — z.object с бизнес-фильтрами (все optional).
 * @param sortFields — readonly tuple допустимых полей: `['createdAt','name'] as const`.
 * @param opts.maxLimit — потолок для defense-in-depth (default 10_000).
 */
const crudBoundedListQueryBody = (filtersSchema, sortFields, opts = {}) => {
    const maxLimit = opts.maxLimit ?? 10_000;
    return filtersSchema.extend({
        limit: zod_1.z.number().int().positive().max(maxLimit).optional(),
        offset: zod_1.z.number().int().nonnegative().optional(),
        sort: zod_1.z.array(crudSortEntrySchema(sortFields)).min(1).optional(),
    });
};
exports.crudBoundedListQueryBody = crudBoundedListQueryBody;
/**
 * Body unbounded-list query: limit/offset/sort REQUIRED. История-like
 * entity без обязательной пагинации = OOM на client-side. Default limit max — 500.
 */
const crudUnboundedListQueryBody = (filtersSchema, sortFields, opts = {}) => {
    const maxLimit = opts.maxLimit ?? 500;
    return filtersSchema.extend({
        limit: zod_1.z.number().int().positive().max(maxLimit),
        offset: zod_1.z.number().int().nonnegative(),
        sort: zod_1.z.array(crudSortEntrySchema(sortFields)).min(1),
    });
};
exports.crudUnboundedListQueryBody = crudUnboundedListQueryBody;
// ── get/delete query bodies — простые id-схемы (для удобства) ──
exports.crudIdParamsBody = zod_1.z.object({ id: zod_1.z.string() });
// ── Domain-level reply builders (принимают Domain, компонуют Snapshot внутри) ──
/**
 * `crudCreateReplyBody` но принимает Domain (бизнес-поля).
 * Внутри делает `composeSnapshot(domain)` → reply `{isOk:true, item: Snapshot}`.
 */
const crudCreateReply = (domain, errorCodes, extraOkFields) => (0, exports.crudCreateReplyBody)((0, compose_1.composeSnapshot)(domain), errorCodes, extraOkFields);
exports.crudCreateReply = crudCreateReply;
/**
 * `crudGetReplyBody` но принимает Domain.
 * Внутри `composeSnapshot(domain)` → reply `{isOk:true, item: Snapshot | null}`.
 */
const crudGetReply = (domain) => (0, exports.crudGetReplyBody)((0, compose_1.composeSnapshot)(domain));
exports.crudGetReply = crudGetReply;
/**
 * `crudListReplyBody` но принимает Domain.
 * Внутри `composeSnapshot(domain)` → reply items: Snapshot[].
 */
const crudListReply = (domain, errorCodes) => (0, exports.crudListReplyBody)((0, compose_1.composeSnapshot)(domain), errorCodes);
exports.crudListReply = crudListReply;
/**
 * `crudUpdateReplyWithItemBody` но принимает Domain.
 * Внутри `composeSnapshot(domain)` → reply `{isOk:true, item: Snapshot}`.
 */
const crudUpdateReply = (domain, errorCodes) => (0, exports.crudUpdateReplyWithItemBody)((0, compose_1.composeSnapshot)(domain), errorCodes);
exports.crudUpdateReply = crudUpdateReply;
//# sourceMappingURL=reply-helpers.js.map